// Settings › Price book to and from a spreadsheet: "Download as a spreadsheet" (every item, as CSV,
// pricebook.exportRows) and "Import from a spreadsheet…" (the edited CSV back: a preview of what's new,
// what changes and what's wrong, pricebook.previewImport, then all of it at once, pricebook.import).
// CSV only: Excel and Sheets both save one.

import { useState } from 'react';
import { client } from '../api.ts';
import { Sheet } from '../shared/EditSheet.tsx';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import { refusal } from '../history/customer-edit-model.ts';
import {
  changeWords,
  exportCsv,
  FIELD_WORDS,
  importRefusal,
  importRows,
  tooBig,
  type ExportItem,
  type ImportRow,
} from './pricebook-sheet-model.ts';
import './pricebook-sheet.css';

interface Preview {
  summary: {
    fileRows: number;
    new: number;
    changed: number;
    unchanged: number;
    problems: number;
    warnings: number;
  };
  rows: {
    line: number;
    code: string;
    action: 'new' | 'changed' | 'unchanged' | 'problem';
    changes: { field: string; from: string | null; to: string | null }[];
    problems: string[];
    warnings: string[];
  }[];
}

export function PricebookSpreadsheet({
  canEdit,
  onImported,
}: {
  canEdit: boolean;
  onImported: (said: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ items: ExportItem[] }>('pricebook.exportRows', {});
      const csv = exportCsv(
        r.items,
        r.items.some((i) => i.costCents !== undefined),
      );
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      a.download = `price-book-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setError(refusal(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="row pb-sheet-acts">
        <button className="btn quiet" disabled={busy} onClick={() => void download()}>
          {busy ? 'Getting the price book…' : 'Download as a spreadsheet'}
        </button>
        {canEdit && (
          <button className="btn quiet" onClick={() => setImporting(true)}>
            Import from a spreadsheet…
          </button>
        )}
      </div>
      {error && <p className="error small">{error}</p>}
      {importing && (
        <ImportSheet
          onClose={() => setImporting(false)}
          onDone={(said) => {
            setImporting(false);
            onImported(said);
          }}
        />
      )}
    </>
  );
}

function ImportSheet({ onClose, onDone }: { onClose: () => void; onDone: (said: string) => void }) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<string | null>(null);
  const [read, setRead] = useState<{ rows: ImportRow[]; fileRows: number; unknown: string[] } | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveKey(`pb-import:${text.length}:${text.slice(0, 200)}`);
  const check = async (csv: string) => {
    setPreview(null);
    setError(null);
    const r = importRows(csv);
    if (r.error) return setError(r.error);
    if (!r.rows.length) return setError('The file has a header but no rows.');
    if (r.rows.length > 2000) return setError('That’s more than 2,000 rows: split it into smaller files.');
    const big = tooBig({ rows: r.rows, fileRows: r.fileRows });
    if (big) return setError(big);
    setRead(r);
    setBusy(true);
    try {
      setPreview(
        await client.call<Preview>('pricebook.previewImport', { rows: r.rows, fileRows: r.fileRows }),
      );
    } catch (e) {
      setError(importRefusal(e));
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    if (!read) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<Preview>(
        'pricebook.import',
        { rows: read.rows, fileRows: read.fileRows },
        { idempotencyKey: save.key },
      );
      const s = r.summary;
      onDone(
        `Price book updated: ${s.new} added, ${s.changed} changed${s.unchanged ? `, ${s.unchanged} already as the file has them` : ''}.`,
      );
    } catch (e) {
      setError(importRefusal(e));
    } finally {
      setBusy(false);
    }
  };
  const s = preview?.summary;
  const shown = preview?.rows.filter((r) => r.action !== 'unchanged') ?? [];
  const changes = s ? s.new + s.changed : 0;
  return (
    <Sheet title="Import the price book from a spreadsheet" wide onClose={onClose}>
      <p className="muted small">
        Start from “Download as a spreadsheet”, change prices or add rows, then save it as CSV (in Excel: File
        › Save As › CSV; in Sheets: File › Download › CSV). Rows are matched by their code: a new code adds an
        item. Leave a column out to keep that field as it is.
      </p>
      <label className="f">
        The CSV file
        <input
          className="field"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f)
              void f.text().then((t) => {
                setText(t);
                setFile(f.name);
                void check(t);
              });
          }}
        />
      </label>
      <details className="pb-paste">
        <summary className="small">Or paste the rows</summary>
        <label className="f">
          Rows, with the header first
          <textarea
            className="field area"
            rows={6}
            spellCheck={false}
            value={file ? '' : text}
            onChange={(e) => {
              setText(e.target.value);
              setFile(null);
            }}
          />
        </label>
        <button className="btn soft" disabled={!text.trim() || busy} onClick={() => void check(text)}>
          Check the rows
        </button>
      </details>
      {busy && !preview && <p className="muted small">Checking {file ?? 'the rows'}…</p>}
      {error && (
        <p className="error small" role="alert">
          {error}
        </p>
      )}
      {!s && !busy && (
        // before a preview (nothing chosen yet, or a file refused): a way out without Escape
        <div className="row">
          <button type="button" className="btn quiet" onClick={onClose}>
            Cancel
          </button>
        </div>
      )}
      {read?.unknown.length ? (
        <p className="muted small">Columns not used: {read.unknown.join(', ')}.</p>
      ) : null}
      {s && (
        <div className="pb-preview" role="region" aria-label="What the import would do">
          <p className="pb-summary">
            <b>{s.new}</b> new · <b>{s.changed}</b> changed · <b>{s.unchanged}</b> unchanged
            {s.problems > 0 && (
              <>
                {' '}
                · <b className="bad">{s.problems}</b> with problems
              </>
            )}
            {s.warnings > 0 && (
              <>
                {' '}
                · <b>{s.warnings}</b> to know about
              </>
            )}
          </p>
          {shown.length > 0 && (
            <ul className="pb-rows">
              {shown.map((r) => (
                <li key={`${r.line}-${r.code}`} className={`pb-row ${r.action}`}>
                  <span className="small">
                    <b>{r.code || `Row ${r.line}`}</b>{' '}
                    <span
                      className={`chip say ${r.action === 'problem' ? 'bad' : r.action === 'new' ? 'ok' : ''}`}
                    >
                      {r.action === 'problem' ? 'Problem' : r.action === 'new' ? 'New' : 'Changed'}
                    </span>
                  </span>
                  {r.changes.map((c) => (
                    <span key={c.field} className="small">
                      {FIELD_WORDS[c.field] ?? c.field}:{' '}
                      {r.action === 'new'
                        ? changeWords(c.field, c.to)
                        : `${changeWords(c.field, c.from)} → ${changeWords(c.field, c.to)}`}
                    </span>
                  ))}
                  {r.problems.map((p) => (
                    <span key={p} className="small error">
                      {p}
                    </span>
                  ))}
                  {r.warnings.map((w) => (
                    <span key={w} className="small warn-text">
                      {w}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          )}
          <div className="row">
            <SaveButton
              busy={busy}
              busyLabel="Importing…"
              error={null}
              disabled={s.problems > 0 || changes === 0}
              onClick={() => void apply()}
            >
              {s.problems > 0
                ? 'Fix the problems first'
                : changes === 0
                  ? 'Nothing to change'
                  : `Apply ${changes === 1 ? 'the change' : `${changes} changes`}`}
            </SaveButton>
            <button type="button" className="btn quiet" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
